import { Link } from '@tanstack/react-router';
import { useAuthStore } from '../store/auth.ts';
import { ThemeToggle } from './ThemeToggle.tsx';
import { Icon } from './Icon.tsx';
import { dv } from '../i18n/dv.ts';
import { displayName } from '../lib/utils.ts';

// Material 3 top app bar (desktop) — logo + wordmark/org on the inline-start
// (right, RTL); notifications / theme / sign-out / avatar on the inline-end.
// Spans the full width above the navigation drawer. Mirrors WapSign's TopBar.
export function TopBar(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  if (!user) return null;
  const shownName = displayName(user);
  const initials = (shownName || user.email).slice(0, 2).toUpperCase();

  return (
    <header
      className="hidden md:flex fixed top-0 inset-x-0 z-40 h-16 items-center gap-2 ps-3 pe-8 bg-[hsl(var(--chrome))]"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <Link to="/" className="flex items-center gap-3 ms-2 min-w-0">
        <img src="/icons/wapmeet-mark.svg" alt="" className="size-10 shrink-0 object-contain" />
        <span className="flex min-w-0 flex-col">
          <span className="wordmark-kolhu text-2xl text-ink leading-tight whitespace-nowrap">{dv.appName}</span>
          {user.orgName && (
            <span className="text-xs text-muted-foreground truncate leading-tight">{user.orgName}</span>
          )}
        </span>
      </Link>

      <div className="flex-1" />

      <div className="flex items-center gap-1 shrink-0">
        <ThemeToggle />
        <button
          onClick={logout}
          aria-label={dv.auth.logout}
          className="grid size-11 place-items-center rounded-full text-muted-foreground hover:bg-muted/70 transition-colors"
        >
          <Icon name="logout" size={22} />
        </button>
        <Link
          to="/profile"
          aria-label={dv.nav.profile}
          title={shownName || undefined}
          className="grid size-9 ms-1 place-items-center rounded-full bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))] text-sm font-medium"
        >
          {initials}
        </Link>
      </div>
    </header>
  );
}
